import { Info } from "@mui/icons-material";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { Avatar, Button } from "@mui/material";
import debounce from "debounce";
import React, { useEffect, useState } from "react";
import { useAllUsers } from "../../common/contexts/allUsersContext";
import { useUser } from "../../common/contexts/userContext";
import { getLeagueRequests, joinLeague } from "../../services/leagueService";
import { League } from "../../types/League";
import { RequestLeague } from "../../types/RequestLeague";
import { truncateString } from "../../utils/textUtils";
import "./LeagueStyles.scss";

interface LeagueInfoProps {
  currentLeague: League | undefined;
  width?: string;
  buttonHeight?: string;
  truncateValue?: number;
  height?: string;
  isDesktop?: boolean;
}

const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  accent?: boolean;
}> = ({ label, value, accent }) => (
  <div className="leagueInfo__stat">
    <span className="leagueInfo__statLabel">{label}</span>
    <span
      className={`leagueInfo__statValue${
        accent ? " leagueInfo__statValue--accent" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

const LeagueInfo: React.FC<LeagueInfoProps> = ({
  currentLeague,
  width,
  truncateValue,
  height,
  isDesktop,
}) => {
  const { user } = useUser();
  const { users } = useAllUsers();
  const admin = users.filter((item) => item.id === currentLeague?.adminId)[0];
  const [userRequests, setUserRequests] = useState<RequestLeague[]>([]);
  // Only decide on the Join button once the league has actually loaded — while
  // currentLeague is undefined the membership check matches nothing, which used
  // to flash "Join League" for members before the data arrived.
  const isMember =
    !!currentLeague &&
    (user.leagues ?? []).some((league) => league.id === currentLeague.id);
  const showButton = !!currentLeague && !isMember;

  const [label, setLabel] = useState("");

  function sendUserJoinRequest() {
    if (!currentLeague) return;
    joinLeague(user.id, currentLeague.id)
      .then(() => setLabel("Request sent"))
      .catch(() => alert("Something went wrong :( "));
  }

  useEffect(() => {
    if (currentLeague) {
      const response = getLeagueRequests(currentLeague?.id);
      response.then((data) => setUserRequests(data));
    }
  }, [currentLeague]);

  useEffect(() => {
    setLabel(
      userRequests.filter((item) => item.userId === user.id)?.length > 0
        ? "Request sent"
        : "Join League"
    );
  }, [userRequests, user.id]);

  const joined = label !== "Join League";

  return (
    <div
      className={`leagueInfo__card${isDesktop ? " leagueInfo__card--desktop" : ""}`}
      style={{ width: width || "90%", minHeight: height || "400px" }}
    >
      <div className="leagueInfo__header">
        <Avatar
          src={currentLeague?.leagueImageUrl}
          className="leagueInfo__avatar"
        >
          {currentLeague?.name?.charAt(0)}
        </Avatar>
        <h2 className="leagueInfo__title">
          {truncateString(
            truncateValue ? truncateValue * 2 : 20,
            currentLeague?.name
          )}
        </h2>
      </div>

      <div className="leagueInfo__statGrid">
        <StatTile
          label="Founder"
          value={truncateString(truncateValue || 10, admin?.username)}
        />
        <StatTile
          label="Leader"
          value={truncateString(truncateValue || 10, currentLeague?.topScorer)}
        />
        <StatTile label="Players" value={currentLeague?.usersCount ?? 0} />
        <StatTile
          label="Top score"
          value={`${currentLeague?.topPoints ?? 0} pts`}
          accent
        />
      </div>

      {isDesktop && currentLeague?.description && (
        <p className="leagueInfo__description">
          <Info /> {currentLeague.description}
        </p>
      )}

      {showButton && (
        <Button
          className="leagueInfo__joinBtn"
          endIcon={joined ? <CheckOutlinedIcon /> : undefined}
          disabled={joined}
          onClick={debounce(sendUserJoinRequest, 500)}
        >
          {label}
        </Button>
      )}
    </div>
  );
};

export default LeagueInfo;
